import useAuth from "../../../../hooks/useAuth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../atoms/Spinner";
import { CustomButton } from "../../../atoms/CustomButton";
import { StatusBadge } from "../../../atoms/StatusBadge";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  useGetEntityStatus,
  useUpdateEntityStatus,
} from "../../../../hooks/entity";

const EntityStatusChanger = () => {
  const [t] = useTranslation();

  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);
  const canWriteEntitySettings = permissions.can(
    Cap.ENTITY_SETTINGS,
    Level.WRITE
  );

  const [isChecked, setIsChecked] = useState(false);

  const {
    isLoading,
    isError,
    isSuccess,
    data: res,
    refetch,
  } = useGetEntityStatus(customer_identification, user.entity_identification, {
    onSuccess: (payload) => {
      setIsChecked(payload?.entity_status === "SERVICE_ON");
    },
  });

  const { mutate, isLoading: isUpdating } = useUpdateEntityStatus();

  const handleToggleEntityStatus = () => {
    const checked = !isChecked;
    setIsChecked(checked);

    mutate(
      {
        customer_identification: customer_identification,
        entity_identification: user.entity_identification,
        shouldEnable: checked,
      },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          setIsChecked(isChecked);
        },
      }
    );
  };

  const statusPill = (tone: "on" | "off", label: string) => (
    <StatusBadge
      color={tone === "on" ? "success" : "failure"}
      dot
      border
      shape="full"
      className="shrink-0"
    >
      {label}
    </StatusBadge>
  );

  const renderStatusUi = (status: string) => {
    const onOffButton = canWriteEntitySettings && (
      <CustomButton
        isLoading={isUpdating}
        variant="primary"
        onClick={handleToggleEntityStatus}
        disabled={userEndedSubscription}
        className="self-end md:self-auto"
        title={
          status === "SERVICE_ON"
            ? (t("dashboard.entityStatusChanger.btn-05") as string)
            : (t("dashboard.entityStatusChanger.btn-06") as string)
        }
      />
    );
    switch (status) {
      case "NEW":
        return canWriteEntitySettings ? (
          <CustomButton
            variant="primary"
            onClick={handleToggleEntityStatus}
            isLoading={isUpdating}
            disabled={userEndedSubscription}
            title={t("dashboard.entityStatusChanger.btn-06")}
          />
        ) : (
          statusPill(
            "off",
            t("dashboard.entityStatusChanger.text-06") as string
          )
        );
      case "SERVICE_ON":
        return (
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-end">
            {statusPill(
              "on",
              t("dashboard.entityStatusChanger.text-05") as string
            )}
            {onOffButton}
          </div>
        );
      case "SERVICE_OFF":
        return (
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-end">
            {statusPill(
              "off",
              t("dashboard.entityStatusChanger.text-06") as string
            )}
            {onOffButton}
          </div>
        );
      case "CANCELED":
        return (
          <CustomButton
            variant="primary"
            disabled={userEndedSubscription}
            title={t("dashboard.entityStatusChanger.btn-02")}
          />
        );
      default:
        return (
          <CustomButton
            variant="primary"
            disabled={userEndedSubscription}
            comp={
              <>
                {t("dashboard.entityStatusChanger.btn-03")} <b>{status}</b>
              </>
            }
          />
        );
    }
  };

  let content;

  if (isLoading)
    content = (
      <span translate="no">
        <Spinner size="sm" />
      </span>
    );

  if (isError)
    content = (
      <CustomButton
        variant="secondary"
        onClick={() => refetch()}
        title={t("dashboard.entityStatusChanger.btn-04")}
      />
    );

  if (isSuccess && res?.entity_status) {
    content = renderStatusUi(res.entity_status);
  }

  return (
    <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <p className="text-sm text-gray-600">
        {t("dashboard.entityStatusChanger.title-01")}
      </p>
      {content}
    </div>
  );
};

export default EntityStatusChanger;
